import { useState } from 'react'
import { Alert, Button, Chip, CircularProgress, Divider, Input, Sheet, Stack, Typography } from '@mui/joy'
import SearchIcon from '@mui/icons-material/Search'
import { CrmRecallResult, askCrmAgent } from 'api/oracleApi'

export function AskPanel() {
    const [query, setQuery] = useState('')
    const [result, setResult] = useState<CrmRecallResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const submit = async () => {
        const q = query.trim()
        if (!q) return
        setLoading(true)
        setError('')
        setResult(null)
        try {
            setResult(await askCrmAgent(q))
        } catch {
            setError('Ask failed. Check that the service is running.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Stack gap={2}>
            <Stack gap={0.5}>
                <Typography level="title-md">Ask the Customer KB</Typography>
                <Typography level="body-sm" color="neutral">
                    Ask about a specific customer ("what does ABC Corp need?") or patterns across customers ("which customers complained about ELD sync?").
                </Typography>
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
                <Input
                    sx={{ flex: 1, minWidth: 240 }}
                    placeholder="Ask anything about your customers..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                    endDecorator={<SearchIcon />}
                />
                <Button onClick={submit} loading={loading} disabled={!query.trim()}>
                    Ask
                </Button>
            </Stack>

            {error && (
                <Alert color="danger" size="sm">
                    {error}
                </Alert>
            )}

            {loading && (
                <Stack alignItems="center" py={4}>
                    <CircularProgress />
                </Stack>
            )}

            {result && !loading && (
                <Stack gap={2}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                            {result.answer}
                        </Typography>
                    </Sheet>
                    {result.citations.length > 0 && (
                        <Stack gap={1}>
                            <Divider>
                                <Chip size="sm" variant="soft">
                                    Citations ({result.citations.length})
                                </Chip>
                            </Divider>
                            {result.citations.map((c, i) => (
                                <Sheet key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 'sm' }}>
                                    <Stack gap={0.25}>
                                        <Typography level="body-xs" fontWeight="lg">
                                            {c.fact_id || c.company_id}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {c.content}
                                        </Typography>
                                    </Stack>
                                </Sheet>
                            ))}
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    )
}
